import { format } from 'date-fns'
import DOMPurify from 'dompurify'

import { Avatar, Flex, Heading, Image, Text } from '@buffer-mono/popcorn'

import { AudienceBadge } from './AudienceBadge'
import { ChangelogScheduledBadge } from './ChangelogScheduledBadge'
import type { ChangelogEntryAudience } from './useChangelog'

import styles from './ChangelogEntryContent.module.css'

import type { JSX } from 'react'

export const DEFAULT_COVER_IMAGE_URL =
  'https://buffer-media-uploads.s3.amazonaws.com/64b6b49e25aaa1bc35091177/uploads/1766065090846/480a26b7-53ac-42b4-91eb-fd0d316ea4e3.png'

interface ChangelogEntryAuthor {
  name: string
  avatar: string
}

interface ChangelogEntryContentProps {
  /** Entry title */
  title: string
  /** HTML content */
  content: string
  /** Optional cover image URL */
  coverImageUrl?: string | null
  /** Publish date - optional for previews. Drives both the displayed timestamp
   *  and ordering throughout the app. */
  publishedAt?: string | Date
  /** Author information */
  author?: ChangelogEntryAuthor | null
  /** Entry audience for badge display */
  audience?: ChangelogEntryAudience | null
  /** When true, shows the "New" badge on general-audience entries. */
  isNew?: boolean
}

/**
 * Reusable component for displaying full changelog entry content.
 * Used in both the entry view dialog and publish confirmation preview.
 */
export function ChangelogEntryContent({
  title,
  content,
  coverImageUrl,
  publishedAt,
  author,
  audience,
  isNew = true,
}: ChangelogEntryContentProps): JSX.Element {
  const imageUrl = typeof coverImageUrl === 'string' ? coverImageUrl : null
  const dateFormat = "MMM d, yyyy 'at' h:mm a"
  const formattedDate = publishedAt
    ? format(new Date(publishedAt), dateFormat)
    : format(new Date(), dateFormat)
  const sanitizedContent = DOMPurify.sanitize(content, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'src',
      'frameborder',
      'allowfullscreen',
      'allow',
      'sandbox',
      'style',
      'target',
      'rel',
    ],
  })

  return (
    <Flex direction="column" gap="md">
      <Image
        src={imageUrl ?? DEFAULT_COVER_IMAGE_URL}
        alt=""
        className={styles.coverImage ?? ''}
      />
      <Flex direction="column" gap="xs">
        <span className={styles.titleRow}>
          <AudienceBadge
            audience={audience}
            isNew={isNew}
            size="medium"
            className={styles.badge}
          />
          <ChangelogScheduledBadge
            publishedAt={publishedAt}
            size="medium"
            className={styles.badge}
          />
          <Heading size="large">{title || 'Untitled'}</Heading>
        </span>
        <Flex align="center" gap="xs">
          <Text color="subtle">{formattedDate}</Text>
          {author && (
            <>
              <Text color="subtle">·</Text>
              <Avatar src={author.avatar} alt={author.name} size="xsmall" />
              <Text color="subtle">{author.name}</Text>
            </>
          )}
        </Flex>
      </Flex>
      {sanitizedContent && (
        <div
          className={styles.entryContent}
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        />
      )}
    </Flex>
  )
}
